import t from "@/lang/lang";
import {
  usePestcontrolMonitorDeleteImageMutation,
  usePestcontrolMonitorDeleteMutation,
  usePestcontrolMonitorImageMutation,
  usePestcontrolMonitorListQuery,
  usePestcontrolMonitorPutMutation,
  usePestcontrolMonitorShowImageQuery,
  usePestcontrolMonitorShowQuery,
} from "@/services/backend/pestcontrol/api/monitor-api";
import {
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestListQuery,
} from "@/services/backend/pestcontrol/api/pest-api";
import {
  PestcontrolMonitor,
  PestcontrolMonitorType,
} from "@/services/backend/pestcontrol/types/monitor";
import {
  Pest,
  PestGroup,
  PestGroupSnapshot,
  PestKind,
  PestSnapshot,
} from "@/services/backend/pestcontrol/types/pest";
import {
  comparePests,
  formatMonitorType,
  groupToSnapshot,
  groupsFromPests,
  mergeGroups,
  normalizeKey,
  normalizeMonitor,
  normalizeMonitorForSave,
  pestToSnapshot,
} from "@/services/backend/pestcontrol/lib/monitor";
import { PestcontrolField } from "@/services/backend/pestcontrol/components/pestcontrol-field";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import {
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { ImageUpload } from "@/shared/components/images/image-upload";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { ChevronLeft, ChevronRight, Loader, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";

export function PestcontrolMonitorEditor({
  monitorId,
  companyId,
  onNavigateMonitor,
  onCancel,
  onDeleted,
}: {
  monitorId: string;
  companyId: string;
  onNavigateMonitor: (monitorId: string) => void;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { data, error, isLoading } = usePestcontrolMonitorShowQuery(
    { id: monitorId },
    { refetchOnMountOrArgChange: true },
  );
  const { data: listData } = usePestcontrolMonitorListQuery({
    company: { active: true, values: [companyId] },
    orderBy: [{ field: "name" }],
    limit: 500,
  });
  const [putMonitor, putState] = usePestcontrolMonitorPutMutation();
  const [deleteMonitor, deleteState] = usePestcontrolMonitorDeleteMutation();
  const { data: pestData } = usePestcontrolPestListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
  });
  const { data: groupData } = usePestcontrolPestGroupListQuery({
    company: { active: true, values: [companyId] },
    limit: 500,
  });
  const [draft, setDraft] = useState<PestcontrolMonitor | null>(null);

  useEffect(() => {
    if (data) {
      setDraft(normalizeMonitor(data));
    }
  }, [data]);

  const pests = useMemo(
    () => [...(pestData?.items ?? [])].sort(comparePests),
    [pestData?.items],
  );
  const groups = useMemo(
    () =>
      [...(groupData?.items ?? [])].sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    [groupData?.items],
  );
  const sortedMonitorIds = useMemo(
    () =>
      [...(listData?.items ?? [])]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((monitor) => monitor.id),
    [listData?.items],
  );
  const currentIndex = sortedMonitorIds.indexOf(monitorId);
  const lockedGroupIds = useMemo(
    () =>
      new Set(
        draft?.pests.flatMap((pest) =>
          pest.group ? [pest.group.sourceId] : [],
        ) ?? [],
      ),
    [draft?.pests],
  );

  if (error) {
    return (
      <CardContent>
        <RTKQueryErrorAlert error={error} />
      </CardContent>
    );
  }

  if (isLoading || !draft) {
    return (
      <CardContent>
        <div className="flex w-full justify-center p-6">
          <Loader className="animate-spin" />
        </div>
      </CardContent>
    );
  }

  const canSave = draft.name.trim().length > 0 && !putState.isLoading;

  const save = async () => {
    if (!canSave) return;
    const key = normalizeKey(draft.key || draft.name);
    try {
      await putMonitor({
        companyId,
        monitor: normalizeMonitorForSave({
          ...draft,
          companyId,
          key,
          name: draft.name.trim(),
          supplier: draft.supplier.trim(),
          details: {
            ...draft.details,
            description: draft.details.description.trim(),
            installationType: draft.details.installationType.trim(),
          },
        }),
        pestIds: draft.pests.map((pest) => pest.sourceId),
        groupIds: draft.groups
          .filter((group) => !lockedGroupIds.has(group.sourceId))
          .map((group) => group.sourceId),
      }).unwrap();
    } catch {
      return;
    }
  };

  const remove = async () => {
    await deleteMonitor({ id: monitorId }).unwrap();
    onDeleted();
  };

  const goPrev = () => {
    if (currentIndex <= 0) return;
    onNavigateMonitor(sortedMonitorIds[currentIndex - 1]);
  };

  const goNext = () => {
    if (currentIndex < 0 || currentIndex >= sortedMonitorIds.length - 1) return;
    onNavigateMonitor(sortedMonitorIds[currentIndex + 1]);
  };

  return (
    <>
      <CardHeader className="relative">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>
            {formatMonitorType(draft.type)} {t("Details")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goPrev}
                    disabled={currentIndex <= 0}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Vorheriger")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goNext}
                    disabled={
                      currentIndex < 0 ||
                      currentIndex >= sortedMonitorIds.length - 1
                    }
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Nächster")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Stammdaten")}
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <PestcontrolField label={t("Name")}>
                  <Input
                    value={draft.name}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        name: event.target.value,
                      })
                    }
                  />
                </PestcontrolField>
                <PestcontrolField label={t("Bezeichner")}>
                  <Input value={draft.key} disabled />
                </PestcontrolField>
                <PestcontrolField label={t("Typ")}>
                  <Select
                    value={draft.type}
                    onValueChange={(value) =>
                      setDraft({
                        ...draft,
                        type: value as PestcontrolMonitorType,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(PestcontrolMonitorType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatMonitorType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </PestcontrolField>
                <PestcontrolField label={t("Lieferant")}>
                  <Input
                    value={draft.supplier}
                    onChange={(event) =>
                      setDraft({ ...draft, supplier: event.target.value })
                    }
                  />
                </PestcontrolField>
              </div>
            </div>
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Zeiten und Preise")}
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <PestcontrolField label={t("Installationszeit")}>
                  <TimeNumberInput
                    value={draft.times.install}
                    onChange={(install) =>
                      setDraft({
                        ...draft,
                        times: { ...draft.times, install },
                      })
                    }
                  />
                </PestcontrolField>
                <PestcontrolField label={t("Servicezeit")}>
                  <TimeNumberInput
                    value={draft.times.maintain}
                    onChange={(maintain) =>
                      setDraft({
                        ...draft,
                        times: { ...draft.times, maintain },
                      })
                    }
                  />
                </PestcontrolField>
                <PestcontrolField label={t("Einkaufspreis")}>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.buyPrice}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        buyPrice: Number(event.target.value) || 0,
                      })
                    }
                  />
                </PestcontrolField>
                <PestcontrolField label={t("Empfohlene Maßnahmen")}>
                  <Input
                    type="number"
                    min={0}
                    value={draft.recommendedVisits}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        recommendedVisits: Number(event.target.value) || 0,
                      })
                    }
                  />
                </PestcontrolField>
              </div>
            </div>
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Schädlinge")}
              </h3>
              <PestAssignment
                pests={pests}
                selected={draft.pests}
                onChange={(selectedPests) =>
                  setDraft({ ...draft, pests: selectedPests })
                }
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">{t("Bild")}</h3>
              <MonitorImagePanel monitor={draft} />
            </div>
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Beschreibung")}
              </h3>
              <PestcontrolField label={t("Beschreibung")}>
                <Textarea
                  value={draft.details.description}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      details: {
                        ...draft.details,
                        description: event.target.value,
                      },
                    })
                  }
                />
              </PestcontrolField>
            </div>
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Schädlingsgruppen")}
              </h3>
              <GroupSelection
                groups={groups}
                selected={draft.groups}
                lockedGroupIds={lockedGroupIds}
                lockedGroups={groupsFromPests(draft.pests)}
                onChange={(selectedGroups) =>
                  setDraft({ ...draft, groups: selectedGroups })
                }
              />
            </div>
            <div className="rounded-md border p-4">
              <h3 className="mb-4 text-base font-semibold">
                {t("Eigenschaften")}
              </h3>
              <div className="grid gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="permanent-baiting"
                    checked={draft.capabilities.permanentBaiting}
                    onCheckedChange={(checked) =>
                      setDraft({
                        ...draft,
                        capabilities: {
                          ...draft.capabilities,
                          permanentBaiting: checked === true,
                        },
                      })
                    }
                  />
                  <Label htmlFor="permanent-baiting">
                    {t("Dauerbeköderung")}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has-lethal-agent"
                    checked={draft.capabilities.hasLethalAgent}
                    onCheckedChange={(checked) =>
                      setDraft({
                        ...draft,
                        capabilities: {
                          ...draft.capabilities,
                          hasLethalAgent: checked === true,
                        },
                      })
                    }
                  />
                  <Label htmlFor="has-lethal-agent">{t("Mit Wirkstoff")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="checkpoint-visual"
                    checked={draft.capabilities.checkpointVisual}
                    onCheckedChange={(checked) =>
                      setDraft({
                        ...draft,
                        capabilities: {
                          ...draft.capabilities,
                          checkpointVisual: checked === true,
                        },
                      })
                    }
                  />
                  <Label htmlFor="checkpoint-visual">
                    {t("Visueller Prüfpunkt")}
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </div>
        {putState.error && <RTKQueryErrorAlert error={putState.error} />}
        {deleteState.error && <RTKQueryErrorAlert error={deleteState.error} />}
        <div className="mt-4 flex justify-between gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={remove}
            disabled={deleteState.isLoading}
          >
            <Trash2 className="size-4" />
            {t("Löschen")}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("Abbrechen")}
            </Button>
            <Button type="button" onClick={save} disabled={!canSave}>
              {t("Speichern")}
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  );
}

function MonitorImagePanel({ monitor }: { monitor: PestcontrolMonitor }) {
  const { data, isLoading, error } = usePestcontrolMonitorShowImageQuery(
    {
      monitorId: monitor.id,
      fileId: monitor.image?.fileId ?? "",
      filePrefix: monitor.image?.prefix ?? "",
    },
    {
      skip: !monitor.image?.fileId || !monitor.image?.prefix,
    },
  );
  const [upload, uploadState] = usePestcontrolMonitorImageMutation();
  const [deleteImage, deleteState] = usePestcontrolMonitorDeleteImageMutation();

  const uploadFile = (file: File) => {
    void upload({
      monitorId: monitor.id,
      fileId: v4(),
      file,
    });
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <ImageUpload
      id={`monitor-image-${monitor.id}`}
      showImagePreview
      existingImageUrl={data?.objectURL}
      onUpload={uploadFile}
      err={parseRTKQueryError(uploadState.error ?? deleteState.error)}
      disabled={uploadState.isLoading || deleteState.isLoading}
      actions={
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => void deleteImage({ monitorId: monitor.id })}
                disabled={
                  uploadState.isLoading ||
                  deleteState.isLoading ||
                  !monitor.image
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Bild löschen")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
    />
  );
}

function PestAssignment({
  pests,
  selected,
  onChange,
}: {
  pests: Pest[];
  selected: PestSnapshot[];
  onChange: (selected: PestSnapshot[]) => void;
}) {
  const add = (pest: Pest) => {
    if (selected.some((item) => item.sourceId === pest.id)) return;
    onChange([...selected, pestToSnapshot(pest)]);
  };
  const remove = (kind: PestKind, id: string) => {
    onChange(
      selected.filter((item) => !(item.kind === kind && item.sourceId === id)),
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <PestSelector
          kind={PestKind.Category}
          pests={pests}
          selected={selected}
          onSelect={add}
        />
        <SelectedPestsTable
          kind={PestKind.Category}
          selected={selected}
          onDeleteSelected={(id) => remove(PestKind.Category, id)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <PestSelector
          kind={PestKind.Indicator}
          pests={pests}
          selected={selected}
          onSelect={add}
        />
        <SelectedPestsTable
          kind={PestKind.Indicator}
          selected={selected}
          onDeleteSelected={(id) => remove(PestKind.Indicator, id)}
        />
      </div>
    </div>
  );
}

function PestSelector({
  kind,
  pests,
  selected,
  onSelect,
}: {
  kind: PestKind;
  pests: Pest[];
  selected: PestSnapshot[];
  onSelect: (pest: Pest) => void;
}) {
  const selectedIds = new Set(
    selected.filter((pest) => pest.kind === kind).map((pest) => pest.sourceId),
  );
  const available = pests
    .filter((pest) => pest.kind === kind && !selectedIds.has(pest.id))
    .sort(comparePests);

  if (pests.length === 0) {
    return <Skeleton className="h-10 w-[300px]" />;
  }

  const label =
    kind === PestKind.Category
      ? t("Schädlings-Kategorie hinzufügen")
      : t("Schädlings-Indikator hinzufügen");

  return (
    <Select
      value=""
      onValueChange={(id) => {
        const pest = available.find((item) => item.id === id);
        if (pest) {
          onSelect(pest);
        }
      }}
    >
      <SelectTrigger className="w-[300px]">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent align="end">
        {available.map((pest) => (
          <SelectItem key={pest.id} value={pest.id}>
            {pest.name}
            {pest.group ? ` (${pest.group.name})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectedPestsTable({
  kind,
  selected,
  onDeleteSelected,
}: {
  kind: PestKind;
  selected: PestSnapshot[];
  onDeleteSelected: (selectedId: string) => void;
}) {
  const rows = selected
    .filter((pest) => pest.kind === kind)
    .sort((left, right) => left.name.localeCompare(right.name));
  const heading =
    kind === PestKind.Category ? t("Kategorien") : t("Indikatoren");

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{heading}</div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30%]">{t("Name")}</TableHead>
            <TableHead className="w-[20%]">{t("Gruppe")}</TableHead>
            <TableHead className="w-[15%]">{t("Abkürzung")}</TableHead>
            <TableHead className="w-[25%]">{t("Beschreibung")}</TableHead>
            <TableHead className="w-[10%] text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => (
            <TableRow key={item.sourceId}>
              <TableCell className="py-1 font-medium">{item.name}</TableCell>
              <TableCell className="py-1">{item.group?.name || "-"}</TableCell>
              <TableCell className="py-1">{item.abbreviation || "-"}</TableCell>
              <TableCell className="py-1">{item.description || "-"}</TableCell>
              <TableCell className="py-1 text-end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="h-fit p-1"
                        size="sm"
                        variant="outline"
                        onClick={() => onDeleteSelected(item.sourceId)}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("Entfernen")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                -- {t("Noch keine Auswahl")} --
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function GroupSelection({
  groups,
  selected,
  lockedGroupIds,
  lockedGroups,
  onChange,
}: {
  groups: PestGroup[];
  selected: PestGroupSnapshot[];
  lockedGroupIds: Set<string>;
  lockedGroups: PestGroupSnapshot[];
  onChange: (selected: PestGroupSnapshot[]) => void;
}) {
  const selectedIds = new Set(selected.map((group) => group.sourceId));
  const selectableGroups = groups
    .map(groupToSnapshot)
    .filter(
      (group) =>
        !selectedIds.has(group.sourceId) && !lockedGroupIds.has(group.sourceId),
    )
    .sort((left, right) => left.name.localeCompare(right.name));
  const selectedRows = mergeGroups(selected, lockedGroups);

  return (
    <div className="flex flex-col gap-3">
      <Select
        value=""
        disabled={selectableGroups.length === 0}
        onValueChange={(id) => {
          const group = selectableGroups.find((item) => item.sourceId === id);
          if (group) {
            onChange(mergeGroups(selected, [group]));
          }
        }}
      >
        <SelectTrigger className="w-[300px]">
          <SelectValue placeholder={t("Schädlingsgruppe hinzufügen")} />
        </SelectTrigger>
        <SelectContent>
          {selectableGroups.map((group) => (
            <SelectItem key={group.sourceId} value={group.sourceId}>
              {group.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Name")}</TableHead>
            <TableHead>{t("Beschreibung")}</TableHead>
            <TableHead className="w-[90px] text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {selectedRows.map((group) => {
            const locked = lockedGroupIds.has(group.sourceId);
            return (
              <TableRow key={group.sourceId}>
                <TableCell className="py-1 font-medium">{group.name}</TableCell>
                <TableCell className="py-1">
                  {group.description || "-"}
                  {locked && (
                    <span className="text-muted-foreground ml-2">
                      {t("durch Schädling gesetzt")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="h-fit p-1"
                          size="sm"
                          variant="outline"
                          disabled={locked}
                          onClick={() =>
                            onChange(
                              selected.filter(
                                (item) => item.sourceId !== group.sourceId,
                              ),
                            )
                          }
                        >
                          <Trash2 className="size-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {locked ? t("durch Schädling gesetzt") : t("Entfernen")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            );
          })}
          {selectedRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center">
                -- {t("Noch keine Auswahl")} --
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {selectableGroups.length === 0 && selectedRows.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t("Keine Schädlingsgruppen verfügbar")}
        </p>
      )}
    </div>
  );
}
